import isString from "lodash/isString";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import React from "react";
import { parseURL } from "ufo";

import { useStableEffect } from "@/lib/useStableEffect";
import { useStableId } from "@/lib/useStableId";

type queryValueT = string | undefined;

let nextUrlInWindowRef: string | undefined;
/**
 * nextUrlInWindowRef – this hack is used to allow changing the URL multiple times synchronously,
 * even before React has updated and the render cycle has completed.
 */

const DEBUG = false;

const stateQueryRegistry: Record<
  string,
  {
    queryKey: string;
    queryValue: string | undefined;
    reactValue: unknown;
    watchers: Record<string, unknown>;
    updateDirection: undefined | "reactToUrl" | "urlToReact";
  }
> = {};

export const useStateQuery = <T>(
  queryKey: string,
  options?: {
    queryToValue?: (value: queryValueT) => T;
    valueToQuery?: (value?: T) => queryValueT;
  },
): [
  T, // This will return the value from the query parameter after being processed by `queryToValue`
  (newValue?: T) => void, // The setter function, which updates the query parameter
  (newValue?: T) => string, // Function to rebuild the URL with the updated query value
] => {
  const {
    queryToValue = (value: queryValueT) => value as T,
    valueToQuery = (value?: T) => value as queryValueT, // Default: convert T to string
  } = options || {};
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryValue = (searchParams.get(queryKey) ?? undefined) as queryValueT;
  const reactValue = queryToValue(queryValue);

  const moduleId = useStableId();

  stateQueryRegistry[queryKey] ??= {
    queryKey,
    queryValue: queryValue,
    reactValue: reactValue,
    watchers: { [moduleId]: {} },
    updateDirection: undefined,
  };
  // Realtime Add Watcher
  stateQueryRegistry[queryKey].watchers[moduleId] ??= {};

  if (
    queryValue !== stateQueryRegistry[queryKey].queryValue &&
    stateQueryRegistry[queryKey].updateDirection !== "reactToUrl"
  ) {
    if (DEBUG) console.log("urlToReact", queryKey, { queryValue, reactValue });
    stateQueryRegistry[queryKey].queryValue = queryValue;
    stateQueryRegistry[queryKey].reactValue = reactValue;
  }

  React.useEffect(() => {
    nextUrlInWindowRef = undefined;
  }, [pathname, searchParams]);

  const buildUrl = React.useCallback(
    (newReactValue?: T) => {
      const [pathname1, search1] = ((): [string, string] => {
        if (!globalThis.window) return [pathname, searchParams.toString()];
        if (nextUrlInWindowRef) {
          const parsedNextUrl = parseURL(nextUrlInWindowRef);
          return [parsedNextUrl.pathname, parsedNextUrl.search];
        }
        return [globalThis.window.location.pathname, globalThis.window.location.search];
      })();
      const params = new URLSearchParams(search1);
      const newQueryValue = valueToQuery(newReactValue);

      for (const key in stateQueryRegistry) {
        applyParam(params, key, stateQueryRegistry[key].queryValue);
      }
      applyParam(params, queryKey, newQueryValue);

      let url = pathname1;
      if (params.toString()) url += "?";
      url += params.toString();

      return url;
    },
    [pathname, searchParams],
  );

  const setValue = React.useCallback(
    (newReactValueRaw?: T) => {
      if (DEBUG)
        console.log(
          "^" + queryKey,
          "setValue",
          structuredClone(stateQueryRegistry[queryKey]),
        );
      // TODO Some warn when no watchers and url will be changes by setValue
      const oldReactValue = stateQueryRegistry[queryKey].reactValue;
      const newReactValue = queryToValue(valueToQuery(newReactValueRaw));
      if (stateQueryRegistry[queryKey].reactValue === newReactValue) return;
      stateQueryRegistry[queryKey].updateDirection = "reactToUrl";
      stateQueryRegistry[queryKey].reactValue = newReactValue;
      stateQueryRegistry[queryKey].queryValue = valueToQuery(newReactValue);
      const newUrl = buildUrl(newReactValue);
      if (nextUrlInWindowRef !== newUrl) {
        if (DEBUG)
          console.error("setValue", "^" + queryKey, {
            newReactValueRaw,
            newReactValue,
            oldReactValue,
            newUrl,
            winQ: globalThis.window.location.href,
          });
        router.replace(newUrl, { scroll: false });
      }
      nextUrlInWindowRef = newUrl;
    },
    [buildUrl],
  );

  React.useEffect(() => {
    const queryValue = searchParams.get(queryKey) ?? undefined;
    if (
      stateQueryRegistry[queryKey].updateDirection === "reactToUrl" &&
      stateQueryRegistry[queryKey].queryValue === queryValue
    ) {
      stateQueryRegistry[queryKey].updateDirection = undefined;
    } else if (stateQueryRegistry[queryKey].updateDirection === "reactToUrl") {
      if (process.env.NODE_ENV === "development") {
        // TODO Maybe need it to debug.
        // TODO In normal case - sometimes react update real url is too laze
        console.error("TODO Force update url for apply stateToUrl", {
          queryKey,
          valInQue: queryValue,
          valInReg: stateQueryRegistry[queryKey].queryValue,
        });
      }
    } else {
      stateQueryRegistry[queryKey].queryValue = queryValue;
      stateQueryRegistry[queryKey].reactValue = queryToValue(queryValue);
    }
  }, [queryKey, searchParams]);

  useStableEffect(
    () => () => {
      delete stateQueryRegistry[queryKey].watchers[moduleId];
      if (DEBUG) console.warn("^" + queryKey, `Drop WATCHER ${moduleId}`);
      if (Object.keys(stateQueryRegistry[queryKey].watchers).length === 0) {
        if (DEBUG) console.warn("^" + queryKey, "no WATCHERS");
        stateQueryRegistry[queryKey].queryValue = undefined;
        stateQueryRegistry[queryKey].reactValue = undefined;
      }
    },
    [queryKey, moduleId],
  );

  return React.useMemo(
    () => [reactValue, setValue, buildUrl],
    [reactValue, setValue, buildUrl],
  );
};

function applyParam(
  params: URLSearchParams,
  queryKey: string,
  queryValue: undefined | null | string,
) {
  if (queryValue === undefined) {
    params.delete(queryKey);
  } else if (queryValue === null) {
    params.set(queryKey, "");
  } else if (isString(queryValue)) {
    params.set(queryKey, queryValue);
  } else {
    console.error('Some mistake in "useStateQuery" code', {
      queryKey,
      queryValue,
    });
  }
}
